/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect } from "react";
import React from "react";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";

// Custom styles for DashboardNavbar
import { navbar, navbarMobileMenu } from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
} from "context";

function DashboardNavbar({ absolute, light, isMini }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;

  useEffect(() => {
    // Keep the top bar static + transparent. The old sticky "frosted" AppBar
    // became an empty white floating block when scrolling (settings/notifications
    // were removed), so we no longer promote it on scroll.
    setTransparentNavbar(dispatch, true);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  return (
    <AppBar
      position="absolute"
      color="inherit"
      sx={(theme) => ({
        ...navbar(theme, {
          transparentNavbar,
          absolute: true,
          light,
          darkMode,
        }),
        // Take no document-flow height — was inflating the gap under StorageLicenseBar.
        top: 0,
        right: 0,
        left: "auto",
        width: "auto",
        minHeight: "0 !important",
        height: "auto",
        padding: 0,
        margin: 0,
        boxShadow: "none",
        backgroundColor: "transparent !important",
        zIndex: 1200,
        "& .MuiToolbar-root": {
          minHeight: "0 !important",
          padding: "0 !important",
        },
      })}
    >
      <Toolbar disableGutters sx={{ minHeight: "0 !important", p: 0 }}>
        {isMini ? null : (
          <IconButton
            size="small"
            disableRipple
            color="inherit"
            aria-label="Open navigation"
            sx={[
              navbarMobileMenu,
              {
                borderRadius: "999px",
                px: 1.25,
                py: 0.5,
                mt: 0.5,
                mr: 0.5,
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(15,23,42,0.85)"
                    : "rgba(15,23,42,0.9)",
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 12px 30px rgba(15,23,42,0.9)"
                    : "0 14px 34px rgba(15,23,42,0.25)",
                "&:hover": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(15,23,42,1)"
                      : "rgba(15,23,42,1)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.18s ease",
              },
            ]}
            onClick={handleMiniSidenav}
          >
            <MenuRoundedIcon
              sx={{
                fontSize: 20,
                color: "#ffffff",
              }}
            />
          </IconButton>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
